import { Cart as CartType } from '../helpers/cart.types'

export class ConditionalCartContentElement extends HTMLElement {
  private static SYNC_EVENT = 'minicart:cart-rendered'
  private static CART_EVENTS = ['renderCartPage', 'renderMiniCart', 'updateCartCount']

  private cartCondition: 'empty' | 'not_empty' = 'empty'
  private cartCount: number = 1
  private updateTimeout: NodeJS.Timeout | null = null
  private cartUpdateHandler: (() => void) | null = null
  private syncHandler: ((event: Event) => void) | null = null
  private isCurrentlyVisible: boolean | null = null
  private lastItemCount: number | null = null

  connectedCallback() {
    this.parseAttributes()
    this.subscribeToCartUpdates()
    this.checkInitialCartState()
  }

  disconnectedCallback() {
    this.unsubscribeFromCartUpdates()
  }

  private parseAttributes() {
    const condition = this.getAttribute('data-cart-condition')
    if (condition === 'empty' || condition === 'not_empty') {
      this.cartCondition = condition
    }

    const count = this.getAttribute('data-cart-count')
    if (count) {
      this.cartCount = parseInt(count, 10) || 1
    }
  }

  private subscribeToCartUpdates() {
    this.syncHandler = (event: Event) => {
      const detail = (event as CustomEvent).detail
      if (detail && typeof detail.itemCount === 'number') {
        if (this.updateTimeout) {
          clearTimeout(this.updateTimeout)
          this.updateTimeout = null
        }
        this.evaluateCondition(detail.itemCount)
      }
    }
    document.addEventListener(ConditionalCartContentElement.SYNC_EVENT, this.syncHandler)

    this.cartUpdateHandler = () => {
      if (this.updateTimeout) {
        clearTimeout(this.updateTimeout)
      }

      this.updateTimeout = setTimeout(() => {
        this.updateVisibility()
      }, 300)
    }

    ConditionalCartContentElement.CART_EVENTS.forEach((event) => {
      document.addEventListener(event, this.cartUpdateHandler!)
    })
  }

  private unsubscribeFromCartUpdates() {
    if (this.syncHandler) {
      document.removeEventListener(ConditionalCartContentElement.SYNC_EVENT, this.syncHandler)
    }

    if (this.cartUpdateHandler) {
      ConditionalCartContentElement.CART_EVENTS.forEach((event) => {
        document.removeEventListener(event, this.cartUpdateHandler!)
      })
    }

    if (this.updateTimeout) {
      clearTimeout(this.updateTimeout)
      this.updateTimeout = null
    }
  }

  private checkInitialCartState() {
    this.updateVisibility()
  }

  private updateVisibility() {
    const cart = window.Cart?.value as CartType | undefined
    if (cart) {
      this.evaluateCondition(cart.item_count || 0)
      return
    }

    const globalCart = (window as any).globals?.cart
    if (globalCart?.initCart) {
      this.evaluateCondition(globalCart.initCart.item_count || 0)
      return
    }

    this.hideContent()
  }

  private evaluateCondition(itemCount: number) {
    let shouldShow = false

    if (this.cartCondition === 'empty') {
      shouldShow = itemCount === 0
    } else if (this.cartCondition === 'not_empty') {
      shouldShow = itemCount >= this.cartCount
    }

    if (this.isCurrentlyVisible === shouldShow && this.lastItemCount === itemCount) {
      return
    }

    this.lastItemCount = itemCount

    if (shouldShow) {
      this.showContent()
    } else {
      this.hideContent()
    }
  }

  private showContent() {
    if (this.isCurrentlyVisible === true) {
      return
    }

    this.style.display = ''
    this.style.visibility = ''
    this.removeAttribute('hidden')
    this.isCurrentlyVisible = true

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.reinitializeNestedComponents()
      })
    })
  }

  private reinitializeNestedComponents() {
    setTimeout(() => {
      this.querySelectorAll<HTMLElement>('.flickity-enabled').forEach((element: any) => {
        try {
          const instance = element.flickity || (window as any).Flickity?.data?.(element)
          if (instance) {
            instance.resize()
            instance.reloadCells()
          }
        } catch {}
      })
    }, 50)
  }

  private hideContent() {
    if (this.isCurrentlyVisible === false) {
      return
    }

    this.style.display = 'none'
    this.isCurrentlyVisible = false
  }
}
